import React, { useState, useRef } from 'react';

interface ErrorHandlingMediaProps {
  src: string;
  alt?: string;
  type: 'image' | 'video';
  className?: string;
  style?: React.CSSProperties;
  controls?: boolean;
  autoPlay?: boolean;
  autoPlayOnHover?: boolean; // New prop for hover autoplay
  autoPlayOnMount?: boolean; // New prop for autoplay when component mounts
  muted?: boolean; // For autoplay to work properly
}

/**
 * A component that wraps images and videos with proper error handling
 * to avoid direct DOM manipulation that can cause React's "removeChild" errors
 */
export const ErrorHandlingMedia: React.FC<ErrorHandlingMediaProps> = ({
  src,
  alt = '',
  type,
  className = '',
  style = {},
  controls = false,
  autoPlay = false,
  autoPlayOnHover = false,
  autoPlayOnMount = false,
  muted = true // Default to muted for autoplay to work
}) => {
  const [error, setError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Auto-play when component mounts (for preview)
  React.useEffect(() => {
    if (autoPlayOnMount && videoRef.current && type === 'video') {
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {
        // Silent fail - autoplay restrictions are common
      });
    }
  }, [autoPlayOnMount, type]);

  // Handlers for hover autoplay
  const handleMouseEnter = () => {
    if (autoPlayOnHover && videoRef.current && type === 'video') {
      // Ensure video is muted for autoplay to work
      videoRef.current.muted = true;
      
      videoRef.current.play().catch(err => {
        // Try to enable muted and play again
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {
            // Silent fail - autoplay restrictions are common
          });
        }
      });
    }
  };

  const handleMouseLeave = () => {
    if (autoPlayOnHover && videoRef.current && type === 'video') {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // Reset to beginning
    }
  };
  
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center bg-gray-200 text-gray-500 text-xs p-2 text-center ${className}`} style={style}>
        {type === 'video' ? (
          <>
            <p>Video available</p>
            <a 
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 px-2 py-1 bg-primary-600 text-white rounded text-[10px] hover:bg-primary-700"
            >
              Open video
            </a>
          </>
        ) : (
          <>
            <p>Failed to load image</p>
            <p className="text-xxs mt-1 text-gray-400">
              {src.substring(0, 20) + (src.length > 20 ? '...' : '')}
            </p>
          </>
        )}
      </div>
    );
  }
  
  if (type === 'video') {
    // If autoPlayOnHover is enabled, wrap in a div to capture hover events
    if (autoPlayOnHover) {
      return (
        <div 
          className={className}
          style={style}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <video
            ref={videoRef}
            src={src}
            className="w-full h-full"
            controls={controls}
            autoPlay={autoPlay || autoPlayOnMount}
            muted={muted}
            loop={autoPlayOnHover} // Loop when hover autoplay is enabled
            playsInline // Better mobile support
            onError={() => {
              console.error(`Video load error for: ${src}`);
              setError(true);
            }}
          />
        </div>
      );
    }
    
    // Regular video without hover autoplay
    return (
      <video
        ref={videoRef}
        src={src}
        className={className}
        style={style}
        controls={controls}
        autoPlay={autoPlay || autoPlayOnMount}
        muted={muted}
        playsInline // Better mobile support
        onError={() => {
          console.error(`Video load error for: ${src}`);
          setError(true);
        }}
      />
    );
  }
  
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => {
        console.error(`Image load error for: ${src}`);
        setError(true);
      }}
    />
  );
}; 